import './TabControl.scss';
import NP, {EventArgs} from "../../NP";
import Control from "../../Control";
import Delegate from "../../Delegate";

export type TabItem = {
	RK?: string;
	Value: any;
	Content?: string;
}

export default class TabControl extends Control {

	public EvTabSelected: Delegate;

	private _SelectedIndex: number;
	private _MinTabWidth: number;
	private _FullHeight: boolean;
	private _Items: Array<TabItem>;

	public EvItemContextMenu: Delegate;

	constructor(settings: object)
	{
		super(settings);
		this._render();
	}

	_initFields(settings: object)
	{
		this._Items = [];
		this._SelectedIndex = 0;
		this.EvItemContextMenu = new Delegate();
		this.EvTabSelected = new Delegate();
		this._MinTabWidth = null;
		this._FullHeight = false;
		super._initFields(settings);
	}

	_render()
	{
		this.np_render(`<div class="tab-bar"><div class="tab-scroller">
    <div class="tab-scroller__scroll-area">
      <div class="tab-scroller__scroll-content"></div></div></div><span class="tab-bar__indicator"></span></div>`);
		this.setItems(this._Items);
		this.setTabIndex(2);

		//if (NP.IsEdge)
		//	this.addClass('tab-edge');

		if (this._FullHeight)
			this.addClass('tab-bar--full-height');
	}

	_getItemHTML(text: string, index: number)
	{
		return `<button class="tab ${this._SelectedIndex == index ? 'tab--active' : ''}" index="${index}">
          <span class="tab__content">
            <span class="tab__text-label">${text}</span>
          </span>
          <span class="tab-indicator ${this._SelectedIndex == index ? 'tab-indicator--active' : ''}">
            <span class="tab-indicator__content tab-indicator__content--underline"></span>
          </span>
        </button>`;
	}

	_getTabItem(node: HTMLElement)
	{
		while (node != this._root)
		{
			if (node.classList.contains("tab"))
				return node;
			node = node.parentNode as HTMLElement;
		}
	}

	getTabNode(index: number)
	{
		return this._root.querySelector(`.tab[index="${index}"]`);
	}

	getTabNodeIndicator(el: HTMLElement)
	{
		return el.querySelector('.tab-indicator');
	}

	getActiveTabNode()
	{
		return this._root.querySelector('.tab.tab--active');
	}

	_onContextMenu(sender: object, args: EventArgs)
	{
		let el = this._getTabItem(args.Event.target as HTMLElement);
		if (el)
		{
			let idx = parseInt(el.getAttribute("index"));
			if (!isNaN(idx))
				this.EvItemContextMenu.fire(this, {Tab: this._Items[idx], Node: el, Args: args});
		}
	}

	setItems(items: Array<TabItem>, activeIndex: number = this._SelectedIndex)
	{
		this._Items = items;
		if (this._Items.length)
		{
			this._SelectedIndex = activeIndex;
			let contentNode = this._root.querySelector('.tab-scroller__scroll-content');
			this._Items.forEach((item, index) =>
			{
				let text = item.Content || NP.RM.getString(item.RK) || '',
					 html = this._getItemHTML(text, index);

				const tab = NP.parseHTMLC(html);
				tab.addEventListener('click',
					 (e: MouseEvent) => this.setSelectedIndex(parseInt((e.target as HTMLElement).getAttribute('index'))));
				contentNode.appendChild(tab);
			});
		}
	}

	scrollTo(value: any)
	{
		let idx = this._Items.findIndex(item => item.Value == value);
		this._root.scrollTo({ left: (this.getTabNode(idx) as HTMLElement).getBoundingClientRect().left});
	}

	clear()
	{
		(this._root.querySelector(".tab-scroller__scroll-content") as HTMLElement).innerHTML = '';
	}

	setTabEnabled(index: number, value: boolean)
	{
		let node = this.getTabNode(index)
		if (value)
			node.removeAttribute("disabled");
		else
			node.setAttribute("disabled", "disabled");
	}

	setTabVisible(index: number, value: boolean)
	{
		if (index == -1)
			index = this._Items.length - 1;
		NP.setIsVisible(this.getTabNode(index), value);
	}

	setTabVisibleByValue(value: object, vis: boolean)
	{
		this.setTabVisible(this._Items.findIndex(item => item.Value == value), vis);
	}

	updateCaption(index: number, caption: string)
	{
		this._root.querySelector(`.tab[index="${index}"] .tab__text-label`).innerHTML = caption;
		this._Items[index].Content = caption;
	}

	updateValue(index: number, value: any)
	{
		this._Items[index].Value = value;
	}

	addItem(caption: string, value: object)
	{
		let contentNode = this._root.querySelector('.tab-scroller__scroll-content'),
			 html = this._getItemHTML(caption, this._Items.length);

		contentNode.appendChild(NP.parseHTMLC(html));

		this._Items.push({
			Content: caption,
			Value: value
		});
	}

	updateCaptionByValue(value: any, caption: string)
	{
		let index = NP.findIndex(this._Items, 'Value', value);
		this.updateCaption(index, caption);
	}

	setSelectedIndex(value: number, fireEvent?: boolean, reInit?: boolean)
	{
		if (this._SelectedIndex !== value || reInit)
		{
			this._SelectedIndex = value;

			let tab = this.getActiveTabNode() as HTMLElement;
			NP.removeCssClass(this.getTabNodeIndicator(tab), 'tab-indicator--active');
			NP.removeCssClass(tab, 'tab--active');

			tab = this.getTabNode(value) as HTMLElement;
			NP.addCssClass(this.getTabNodeIndicator(tab), 'tab-indicator--active');
			NP.addCssClass(tab, 'tab--active');

			if (fireEvent !== false)
				this.EvTabSelected.fire(this, {Value: this._Items[value].Value});
		}
	}

	setValue(value: any, fireEvent?: boolean)
	{
		let res = this._Items.find(item => item.Value == value);
		if (res)
			this.setSelectedIndex(this._Items.indexOf(res), fireEvent);
	}

	getValue(): any
	{
		return this._Items[this._SelectedIndex].Value;
	}

	getSelectedIndex()
	{
		return this._SelectedIndex;
	}

	setScrollShadowVisible(value: boolean)
	{
		if (value)
			this.addClass('tab-bar--scroll-shadow');
		else
			this.removeClass('tab-bar--scroll-shadow');
	}
}